'use client';
import { useTranslation } from 'next-i18next';
import { useParams, usePathname, useRouter, useSearchParams } from 'next/navigation';
import { safelyParseJSON } from '@/app/helpers/searchparams';
import { FilterOptionKeys, FilterOptionValue } from '@mybestbrands/types';
import { COOKIE_NAMES } from '@/app/constants';
import { setCookie } from 'cookies-next';
import { getDomainWithoutSubdomain } from '@/app/ui/components/tracking/setGenderCookie';

function getNameFromValue(filterValue: string | number, facet?: FilterOptionValue | null) {
  if (!facet || !facet.options) return filterValue;

  const option = (facet.options || []).find((option) => option.value === filterValue);

  if (!option) return filterValue;

  return option?.name ?? filterValue;
}

export function SelectedFilter({
  name,
  filterKey,
  facet,
}: {
  name: string | number;
  filterKey: FilterOptionKeys;
  facet?: FilterOptionValue | null;
}) {
  const { t } = useTranslation(['filters', 'navigation']);

  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const { slug } = useParams();

  const filterName = getNameFromValue(name, facet);

  const isBrandRetailerPage =
    pathname.indexOf(`/${t('navigation:BRANDS').toLowerCase()}/`) > -1 || pathname.indexOf(`/online-shops/`) > -1;

  function removeFilter(filterKey: FilterOptionKeys, name: string | number) {
    const params = new URLSearchParams(searchParams ?? '');
    const filters = params.get('filters');
    const page = params.get('page');

    const filterParsed = safelyParseJSON(filters);

    let updatedPathname = pathname;

    if (filterKey === FilterOptionKeys.SALE) {
      delete filterParsed.sale;
      // filterParsed.sale = !filterParsed.sale;
    } else if (filterKey === FilterOptionKeys.COUPON) {
      delete filterParsed.coupon;
    } else if (filterKey === FilterOptionKeys.HOTTEST_SALE) {
      delete filterParsed.hottest_sale;
    } else if (filterKey === FilterOptionKeys.PRICE) {
      delete filterParsed.price;
    } else if (filterKey === FilterOptionKeys.GENDERS) {
      delete filterParsed.genders;
      params.delete('gender');
      // deleting domain wide cookie work only if reset expire date to past
      setCookie(COOKIE_NAMES.GENDER, 'diverse', {
        path: '/',
        domain: getDomainWithoutSubdomain(window.location.host),
        expires: new Date('Thu, 01 Jan 1970 00:00:00 GMT'),
      });
      updatedPathname = !isBrandRetailerPage ? `/${slug ? slug + '/' : ''}` : '';
    } else {
      filterParsed[filterKey] = filterParsed[filterKey].filter((v: string | number) => v !== name);
      //delete if it is empty
      if (filterParsed[filterKey].length === 0) delete filterParsed[filterKey];
    }

    //We have to remove the page param when we change touch any filter
    if (page) params.delete('page');

    const newFilters = JSON.stringify(filterParsed);

    if (newFilters === '{}') {
      params.delete('filters');
    } else {
      // //update sort param
      params.set('filters', JSON.stringify(filterParsed));
    }
    const url = `${updatedPathname}?${params.toString()}`;
    //update the url
    if (updatedPathname || params.toString()) router.push(url);
    else {
      router.refresh();
    }
  }

  return (
    <button
      onClick={() => {
        removeFilter(filterKey, name);
      }}
      className="btn btn-grey-hover-black d-inline-block medium me-2 mb-2">
      <span>{t(filterName as string)}</span>
      <span
        className="ms-2"
        style={{
          fontSize: '.6rem',
        }}>
        &#10005;
      </span>
    </button>
  );
}
